import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { createReducer, on, StoreModule } from '@ngrx/store';
import { Equipment } from '@polaris-customerweb/sharedcomponents.garage';
import { EffectLoadingState } from '../shared/models/effect-state';
import { EffectStateBase } from '../shared/models/effect-state-base';
import * as EquipmentActions from './equipment.actions';
import { EquipmentTelemetryDto } from './equipment.models';

export const equipmentFeatureKey = 'equipmentState';

export interface EquipmentState extends EntityState<EquipmentTelemetryDto> {
    equipment: Equipment[];
    activeEquipmentId: string;
    equipmentUpdateState: EffectStateBase;
    equipmentDeleteState: EffectStateBase;
}

export const adapter: EntityAdapter<EquipmentTelemetryDto> =
    createEntityAdapter<EquipmentTelemetryDto>({
        selectId: (spec: EquipmentTelemetryDto) => spec.equipmentId,
    });

export const initialState: EquipmentState = adapter.getInitialState({
    activeEquipmentId: '',
    equipment: [],
    equipmentDeleteState: {
        effectState: EffectLoadingState.Init,
    },
    equipmentUpdateState: {
        effectState: EffectLoadingState.Init,
    },
});

export const equipmentReducer = createReducer(
    initialState,
    on(EquipmentActions.GetEquipmentSuccess, (state, { equipment }) => {
        return {
            ...state,
            equipment,
        };
    }),
    on(EquipmentActions.SetActiveEquipmentId, (state, { equipmentId }) => {
        return {
            ...state,
            activeEquipmentId: equipmentId,
        };
    }),
    on(EquipmentActions.UpdateEquipment, (state) => {
        return {
            ...state,
            equipmentUpdateState: {
                effectState: EffectLoadingState.Loading,
            },
        };
    }),
    on(EquipmentActions.UpdateEquipmentSuccess, (state, { equipment }) => {
        const originalEquipment = state.equipment.filter(
            (equip) => equip.id !== equipment.id
        );
        return {
            ...state,
            equipment: [...originalEquipment, equipment],
            equipmentUpdateState: {
                effectState: EffectLoadingState.Loaded,
            },
        };
    }),
    on(EquipmentActions.UpdateEquipmentFail, (state, { errorMessage }) => {
        return {
            ...state,
            equipmentUpdateState: {
                effectState: { errorMessage },
            },
        };
    }),
    on(EquipmentActions.DeleteEquipment, (state) => {
        return {
            ...state,
            equipmentDeleteState: {
                effectState: EffectLoadingState.Loading,
            },
        };
    }),
    on(EquipmentActions.DeleteEquipmentSuccess, (state, { equipmentId }) => {
        const updatedEquipment = state.equipment.filter(
            (equip) => equip.id !== equipmentId
        );
        return {
            ...state,
            equipment: updatedEquipment,
            equipmentDeleteState: {
                effectState: EffectLoadingState.Loaded,
            },
        };
    }),
    on(EquipmentActions.DeleteEquipmentFail, (state, { errorMessage }) => {
        return {
            ...state,
            equipmentDeleteState: {
                effectState: { errorMessage },
            },
        };
    })
);

export const equipmentStoreModule = StoreModule.forFeature(
    equipmentFeatureKey,
    equipmentReducer
);
